// API Configuration for Job Hero Backend

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api/v1';

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to make API requests
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// Auth API calls
export const authAPI = {
  register: (userData) => apiRequest('/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),
  
  login: (credentials) => apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  }),
  
  logout: () => apiRequest('/auth/logout', {
    method: 'POST',
  }),
  
  getCurrentUser: () => apiRequest('/auth/me'),
  
  refreshToken: () => apiRequest('/auth/refresh', {
    method: 'POST',
  }),
};

// User API calls
export const userAPI = {
  getAllUsers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/users${queryString ? `?${queryString}` : ''}`);
  },
  
  getUser: (id) => apiRequest(`/users/${id}`),
  
  updateUser: (userData) => apiRequest('/users/me', {
    method: 'PUT',
    body: JSON.stringify(userData),
  }),
  
  deleteUser: () => apiRequest('/users/me', {
    method: 'DELETE',
  }),
  
  getUserStats: () => apiRequest('/users/stats'),
};

// Profile API calls
export const profileAPI = {
  getProfile: (id) => apiRequest(`/profiles/${id}`),
  
  updateProfile: (profileData) => apiRequest('/profiles/me', {
    method: 'PUT',
    body: JSON.stringify(profileData),
  }),
  
  followUser: (id) => apiRequest(`/profiles/${id}/follow`, {
    method: 'POST',
  }),
  
  unfollowUser: (id) => apiRequest(`/profiles/${id}/follow`, {
    method: 'DELETE',
  }),
  
  getFollowers: (id) => apiRequest(`/profiles/${id}/followers`),
  
  getFollowing: (id) => apiRequest(`/profiles/${id}/following`),
};

// Post API calls
export const postAPI = {
  getPosts: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/posts${queryString ? `?${queryString}` : ''}`);
  },
  
  getPost: (id) => apiRequest(`/posts/${id}`),
  
  createPost: (postData) => apiRequest('/posts', {
    method: 'POST',
    body: JSON.stringify(postData),
  }),
  
  updatePost: (id, postData) => apiRequest(`/posts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(postData),
  }),
  
  deletePost: (id) => apiRequest(`/posts/${id}`, {
    method: 'DELETE',
  }),
  
  likePost: (id) => apiRequest(`/posts/${id}/like`, {
    method: 'POST',
  }),
  
  unlikePost: (id) => apiRequest(`/posts/${id}/like`, {
    method: 'DELETE',
  }),
  
  getPostLikes: (id) => apiRequest(`/posts/${id}/likes`),
};

// Comment API calls
export const commentAPI = {
  getComments: (postId) => apiRequest(`/posts/${postId}/comments`),
  
  createComment: (postId, text) => apiRequest(`/posts/${postId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  }),
  
  updateComment: (postId, commentId, text) => apiRequest(`/posts/${postId}/comments/${commentId}`, {
    method: 'PUT',
    body: JSON.stringify({ text }),
  }),
  
  deleteComment: (postId, commentId) => apiRequest(`/posts/${postId}/comments/${commentId}`, {
    method: 'DELETE',
  }),
};

// Upload API calls
export const uploadAPI = {
  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    
    const token = getAuthToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/upload/avatar`, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Upload failed');
    }
    
    return response.json();
  },
  
  uploadPostImage: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    
    const token = getAuthToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/upload/post-image`, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Upload failed');
    }
    
    return response.json();
  },
};

export default {
  auth: authAPI,
  user: userAPI,
  profile: profileAPI,
  post: postAPI,
  comment: commentAPI,
  upload: uploadAPI,
};